// ─────────────────────────────────────────────────────────────────────────────
// REQ-097 — Helper de adquisición silenciosa de token para el módulo de Reconocimiento.
//
// MSAL YA está instalado (@azure/msal-browser / @azure/msal-react), así que este helper es
// REAL (no un stub): reutiliza la instancia MSAL compartida + los apiScopes, con el mismo
// fallback a Teams SSO que usa `src/api/client.ts`. Si no hay sesión todavía, devuelve '' —
// una cadena vacía produce un 401 esperado que las capas superiores manejan como estado de error.
// ─────────────────────────────────────────────────────────────────────────────

import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiScopes, msalInstance, waitForMsalAccount } from '@/auth/msalConfig';
import { SessionExpiredError } from '@/auth/errors';
import { notifySessionExpired } from '@/auth/sessionExpiredBus';
import TeamsSDK from '@/teams/TeamsSDK';
import { acquireTokenDeduped } from '@/auth/tokenCache';

/**
 * Adquiere un access token para llamar a las APIs de PMP.
 * - Dentro de Teams: SSO silencioso (getAuthToken); comparte audiencia con las APIs.
 * - Navegador standalone: `acquireTokenSilent` con la cuenta MSAL activa.
 *
 * Fail-soft para el caso normal: devuelve '' si aún no hay sesión o si la adquisición falla por
 * cualquier otra razón. La EXCEPCIÓN es la misma que en `api/client.ts` (bug fila 100, UAT
 * 2026-08-25): si Entra ID exige reautenticación interactiva a mitad de sesión
 * (`InteractionRequiredAuthError`), intenta recuperarse con un popup y, si eso también falla,
 * SÍ lanza `SessionExpiredError` — para que el Muro/perfil/etc. no se queden en 401 silencioso
 * sin ninguna pista de la causa real.
 */
export async function acquireTokenSilentSafe(): Promise<string> {
  // Dedupe de llamadas concurrentes + caché del token mientras esté lejos de expirar. El landing
  // dispara ~8 queries que llamaban aquí por separado; la ruta Teams no deduplica y sacaba ráfagas
  // de `getAuthToken()` (y de cadena vacía → 401) — ver `tokenCache.ts`.
  return acquireTokenDeduped(acquireTokenSilentUncached);
}

async function acquireTokenSilentUncached(): Promise<string> {
  if (TeamsSDK.isInTeams()) {
    try {
      return await TeamsSDK.getSsoToken();
    } catch (e) {
      // Visible siempre para diagnosticar por qué Teams SSO no da token.
      console.error('[Auth] Teams SSO getAuthToken falló:', e);
      return '';
    }
  }

  // Espera acotada a que AuthProvider termine de fijar la cuenta: sin esto, la primera tanda de
  // queries del landing salía sin token → 401 (ver waitForMsalAccount).
  const account = await waitForMsalAccount();
  if (!account) {
    if (import.meta.env.DEV) {
      console.warn('[Auth] MSAL sin cuenta activa todavía — token vacío (401 esperado hasta login)');
    }
    return '';
  }

  try {
    const result = await msalInstance.acquireTokenSilent({ scopes: apiScopes, account });
    return result.accessToken;
  } catch (e) {
    console.error('[Auth] acquireTokenSilent falló:', e);
    if (e instanceof InteractionRequiredAuthError) {
      try {
        const popupResult = await msalInstance.acquireTokenPopup({ scopes: apiScopes, account });
        return popupResult.accessToken;
      } catch (popupError) {
        // datadog-disable-next-line typescript-best-practices/no-console
        // Deliberado, no un debug leftover: única pista visible en producción de por qué la sesión
        // no se pudo recuperar (bug fila 100, UAT 2026-08-25). El propio eslint del proyecto ya
        // permite console.error (`eslint.config.js`, no-console allow: ['warn','error']).
        console.error('[Auth] acquireTokenPopup (recuperación) falló:', popupError);
        notifySessionExpired();
        throw new SessionExpiredError();
      }
    }
    return '';
  }
}
